/*
 * SoflePLUS2 builder UI. Layout: a panel pinned under the store header with the 3D view (left) and the build summary
 * (right: lines, total, estimate, consent, add to cart), and the choices below it at full width (model, socket,
 * parts, switches, keycaps). On phones the 3D stays pinned (smaller), the summary moves below the choices, and a
 * bottom bar keeps the total and the add-to-cart button in reach.
 * The same file runs on the Shopify product page (theme block sp2-configurator) and on the local test page.
 * Needs: SP2Rules (sp2-rules.js), SP2Currency (sp2-currency.js), SofleConfigurator (sofle-configurator.js).
 *
 *   SP2Builder.mount(rootElement, config)
 *
 * config: {
 *   catalog, looks, hotspots,            // catalog/options.json, app/looks.json, build/*_hotspots.json
 *   live,                                // { variantId: { price, compare, available, qty, img } } from Liquid
 *   loadLive(country) -> Promise<live>,  // local test page only: fetch live data for a market
 *   marketSwitch: false,                 // local test page only: market select, rendered into [data-sp2c-market]
 *   country: 'MY',                       // visitor country (Shopify localization)
 *   glbUrl, mode: 'shopify' | 'preview', paypal: true,
 *   text: { consent, trackpadNote, switchGuideUrl, shippingNote },
 *   estimate: { show: true, fallbackUsdRate: 0.25 },
 *   viewer: { light, lite, debug }       // optional overrides
 * }
 * Shell inside the root element: [data-sp2c-panel] > [data-sp2c-stage] (with [data-sp2c-loading],
 * [data-sp2c-caption], [data-sp2c-tools], [data-sp2c-gallery] > [data-sp2c-photo] + [data-sp2c-thumb]...) and
 * [data-sp2c-side]; then [data-sp2c-builder] (choices) and [data-sp2c-bar] (phone bar).
 */
(function (root) {
  'use strict';

  var QTY = 60;
  var PHONE = '(max-width: 900px)';
  var esc = function (s) {
    return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  };
  var fmt = function (cents, opts) { return SP2Rules.formatRM(cents, opts); };
  var money = function (cents) { return fmt(cents, { compact: true }); };
  function pct(price, compare) { return Math.round((compare - price) * 100 / compare); }
  // Shopify's route prefix (e.g. "/" or "/en-sg/" with market subfolders)
  function routeRoot() { return (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/'; }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode: fine */ } }

  function mount(el, cfg) {
    var $ = function (sel) { return el.querySelector(sel); };
    var text = Object.assign({
      consent: 'I understand that with regular shipping (not DDP, delivered duty paid), import duties, taxes, VAT and customs fees are charged to me on delivery.',
      trackpadNote: 'The Large trackpad is included with every build.',
      switchGuideUrl: '',
      shippingNote: 'Shipping calculated at checkout.'
    }, cfg.text || {});
    var estimateCfg = Object.assign({ show: true, fallbackUsdRate: 0.25 }, cfg.estimate || {});
    var country = cfg.country || 'MY';
    var engine = SP2Rules.createEngine(cfg.catalog, cfg.live || {});
    var params = new URLSearchParams(location.search);
    var state = params.get('sp2') ? engine.decode(params.get('sp2')) : engine.initialState();
    var ui = { cart: null, busy: false, feedback: '', feedbackKind: '', liveNote: cfg.loadLive ? 'Loading live prices and stock…' : '', copied: false };
    var rates = null;
    var estCurrency = lsGet('sp2_cur') || SP2Currency.currencyForCountry(country === 'MY' ? 'US' : country);

    var builder = $('[data-sp2c-builder]');
    var side = $('[data-sp2c-side]');
    var bar = $('[data-sp2c-bar]');
    var marketBox = cfg.marketSwitch ? document.querySelector('[data-sp2c-market]') : null;
    var summaryEl = document.createElement('div');
    summaryEl.className = 'sp2c-summary';
    var phone = root.matchMedia ? root.matchMedia(PHONE) : { matches: false, addEventListener: function () {} };
    function placeSummary() {
      if (phone.matches) { if (summaryEl.previousElementSibling !== builder) builder.after(summaryEl); }
      else if (summaryEl.parentNode !== side) side.appendChild(summaryEl);
    }
    placeSummary();
    phone.addEventListener('change', placeSummary);

    // Keep the panel under the store's own sticky header, if it has one showing.
    function headerOffset() {
      var h = document.querySelector('sticky-header, .shopify-section-header-sticky, .section-header, header-component, #shopify-section-header');
      var top = 0;
      if (h) {
        var r = h.getBoundingClientRect();
        var pos = getComputedStyle(h).position;
        if ((pos === 'sticky' || pos === 'fixed') && r.bottom > 0 && r.top <= 0.5) top = Math.round(r.bottom);
      }
      el.style.setProperty('--sp2c-top', top + 'px');
    }
    var offsetQueued = false;
    root.addEventListener('scroll', function () {
      if (offsetQueued) return;
      offsetQueued = true;
      requestAnimationFrame(function () { offsetQueued = false; headerOffset(); });
    }, { passive: true });
    root.addEventListener('resize', headerOffset);
    headerOffset();

    // ---------- 3D and photos ----------
    var viewer = null;
    var stage = $('[data-sp2c-stage]');
    var loading = $('[data-sp2c-loading]');
    var caption = $('[data-sp2c-caption]');
    var tools = $('[data-sp2c-tools]');
    var gallery = $('[data-sp2c-gallery]');
    var photo = $('[data-sp2c-photo]');
    var thumbs = function () { return Array.prototype.slice.call(el.querySelectorAll('[data-sp2c-thumb]')); };
    var TOOLS = [
      ['explode', 'Exploded view', false], ['rgb', 'RGB backlight', false], ['keycaps', 'Keycaps', true],
      ['switches', 'Switches', true], ['view:top', 'Top', null], ['view:bottom', 'Bottom', null], ['view:home', 'Reset view', null],
      ['photos', 'Photos', null]
    ];
    function renderTools() {
      if (!tools) return;
      tools.innerHTML = TOOLS.filter(function (t) { return t[0] !== 'photos' || thumbs().length; }).map(function (t) {
        return '<button type="button" class="sp2c-tool" data-tool="' + t[0] + '"' + (t[2] === null ? '' : ' aria-pressed="' + t[2] + '"') + '>' + t[1] + '</button>';
      }).join('');
    }
    renderTools();
    function press(tool, on) { var b = tools && tools.querySelector('[data-tool="' + tool + '"]'); if (b) b.setAttribute('aria-pressed', on); }
    function syncLayerButtons() {
      if (!tools) return;
      var sel = engine.selection(state);
      ['keycaps', 'switches'].forEach(function (slot) {
        var b = tools.querySelector('[data-tool="' + slot + '"]');
        if (!b) return;
        b.disabled = !viewer || sel[slot].picks.length === 0;
        b.title = sel[slot].picks.length ? '' : 'Pick ' + slot + ' below to see them';
      });
    }
    var photoIndex = 0;
    // without a 3D view (no model link, or WebGL failed) the photos are the stage: no "Back to 3D"
    function photosOnly() {
      var close = el.querySelector('[data-sp2c-gal="close"]');
      if (close) close.hidden = true;
      if (thumbs().length) showPhoto(0);
    }
    function showPhoto(i) {
      var list = thumbs();
      if (!list.length || !gallery) return;
      photoIndex = (i + list.length) % list.length;
      photo.src = list[photoIndex].getAttribute('data-sp2c-thumb');
      photo.alt = (list[photoIndex].querySelector('img') || {}).alt || '';
      list.forEach(function (t, k) { t.classList.toggle('is-active', k === photoIndex); });
      gallery.hidden = false;
    }
    function show3D() { if (gallery) gallery.hidden = true; }

    // Build Kit: a large label at the top left of the viewer. The model stays visible behind it.
    var kitNote = document.createElement('div');
    kitNote.className = 'sp2c-kit-note';
    kitNote.innerHTML = '<span class="sp2c-kit-title">Solder and assembly required</span><span class="sp2c-kit-mock">Mock Up View</span>';
    if (stage) stage.appendChild(kitNote);

    if (cfg.glbUrl && root.SofleConfigurator) {
      var vcfg = cfg.viewer || {};
      SofleConfigurator.createViewer(stage, {
        glb: cfg.glbUrl, looks: cfg.looks, hotspots: cfg.hotspots, light: vcfg.light, lite: vcfg.lite, debug: vcfg.debug,
        onProgress: function (p) { if (loading) loading.textContent = 'Loading 3D… ' + Math.round(p * 100) + '%'; },
        onCaption: function (list) { if (caption) { caption.textContent = list.join(' '); caption.hidden = !list.length; } }
      }).then(function (vw) {
        viewer = vw;
        if (loading) loading.remove();
        viewer.show(engine.selection(state));
        syncLayerButtons();
        root.sp2Viewer = viewer;
        if (cfg.onViewer) cfg.onViewer(viewer, press);
      }).catch(function (err) {
        if (loading) loading.textContent = 'The 3D view couldn’t load on this device.';
        TOOLS = [];
        renderTools();
        photosOnly();
        console.error(err);
      });
    } else {
      TOOLS = [];
      renderTools();
      if (loading) loading.remove();
      photosOnly();
    }

    // ---------- rendering: choices ----------
    function badge(price, compare) {
      return compare ? ' <span class="sp2c-badge">-' + pct(price, compare) + '%</span>' : '';
    }

    function setsRow(ev) {
      var cards = ev.presets.map(function (p) {
        var was = p.displayCompare ? ' <s>' + money(p.displayCompare) + '</s>' : '';
        return '<button type="button" class="sp2c-card sp2c-set' + (p.active ? ' is-on' : '') + '" data-set="' + p.id + '"' + (p.available ? '' : ' disabled') + '>' +
          '<b>' + esc(p.label) + '</b><span class="sp2c-price">' + money(p.displayPrice) + was + badge(p.displayPrice, p.displayCompare) + '</span>' +
          (p.available ? '' : '<span class="sp2c-why">Out of stock</span>') + '</button>';
      }).join('');
      var kit = ev.kit;
      if (kit) {
        cards += '<button type="button" class="sp2c-card sp2c-set' + (kit.active ? ' is-on' : '') + '" data-kit="' + kit.id + '"' + (kit.available ? '' : ' disabled') + '>' +
          '<b>' + esc(kit.label) + '</b><span class="sp2c-price">' + money(kit.displayPrice) + (kit.displayCompare ? ' <s>' + money(kit.displayCompare) + '</s>' : '') + badge(kit.displayPrice, kit.displayCompare) + '</span>' +
          '<span class="sp2c-kit-sub">Solder and assembly required</span>' +
          (kit.available ? '' : '<span class="sp2c-why">Out of stock</span>') + '</button>';
      }
      return '<section class="sp2c-row sp2c-row--wide"><div class="sp2c-label">Model</div><div class="sp2c-opts sp2c-opts--sets">' + cards + '</div></section>';
    }

    // kitOn is passed only for the Build Kit's optional parts: the same cards as a set, plus "Not included".
    function partRow(ev, slot, kitOn) {
      var meta = ev.slots[slot];
      var kit = kitOn !== undefined;
      var cards = ev.options[slot].filter(function (o) { return !kit || o.id !== 'none'; }).map(function (o) {
        var on = kit ? kitOn && o.selected : o.selected;
        var d = on ? '' : kit ? (o.price ? money(o.price) : '') : meta.showPrice && o.delta ? fmt(o.delta, { sign: true, compact: true }) : '';
        var why = o.reason ? '<span class="sp2c-why">' + esc(o.reason) + '</span>' : '';
        return '<button type="button" class="sp2c-card sp2c-opt' + (on ? ' is-on' : '') + '" data-part="' + slot + '" data-id="' + o.id + '"' + (o.available ? '' : ' disabled') + '>' +
          '<span class="sp2c-name">' + esc(o.label) + why + '</span><span class="sp2c-delta">' + d + '</span></button>';
      }).join('');
      return '<section class="sp2c-row"><div class="sp2c-label">' + esc(meta.label) + '</div><div class="sp2c-opts">' + cards + '</div></section>';
    }

    function switchesRow(ev) {
      var picked = ev.options.switches.filter(function (o) { return o.selected; }).length;
      var skipped = state.switchesSkipped;
      var status = skipped ? 'No switches' : picked ? picked + ' type' + (picked > 1 ? 's' : '') + ' · ' + picked * QTY + ' switches' : 'Choose, or skip';
      var cards = ev.options.switches.map(function (o) {
        var why = o.reason ? '<span class="sp2c-why">' + esc(o.reason) + '</span>' : '';
        return '<button type="button" class="sp2c-card sp2c-sw' + (o.selected ? ' is-on' : '') + (o.reason && o.available ? ' is-clash' : '') + '" data-many="switches" data-id="' + o.id + '"' + (o.available ? '' : ' disabled') + '>' +
          '<span class="sp2c-name">' + esc(o.label) + badge(o.price, o.compareAt) + why + '</span>' +
          '<span class="sp2c-delta">' + money(o.price) + ' ea<br><small>' + QTY + ' per set</small></span></button>';
      }).join('');
      var skip = '<button type="button" class="sp2c-card sp2c-skip' + (skipped ? ' is-on' : '') + '" data-skip="switches"><span class="sp2c-name">I don’t need any switches</span></button>';
      var guide = text.switchGuideUrl ? ' <a href="' + esc(text.switchGuideUrl) + '" target="_blank" rel="noopener">Key switches guide</a>' : '';
      return '<section class="sp2c-row sp2c-row--wide" data-sp2c-section="switches"><div class="sp2c-label">Switches <em>' + status + '</em></div>' +
        '<p class="sp2c-help">Sold in sets of ' + QTY + ' per type.' + guide + '</p>' +
        '<div class="sp2c-opts sp2c-opts--sw">' + cards + skip + '</div></section>';
    }

    function keycapsRow(ev) {
      var picked = ev.options.keycaps.filter(function (o) { return o.selected; }).length;
      var skipped = state.keycapsSkipped;
      var status = skipped ? 'No keycaps' : picked ? picked + ' selected' : 'Choose, or skip';
      var cards = ev.options.keycaps.map(function (o) {
        var why = o.reason ? '<span class="sp2c-why">' + esc(o.reason) + '</span>' : '';
        var img = o.img ? '<img src="' + esc(o.img) + '" alt="" loading="lazy">' : '<span class="sp2c-noimg">' + esc(o.label) + '</span>';
        return '<button type="button" class="sp2c-card sp2c-kc' + (o.selected ? ' is-on' : '') + (o.reason && o.available ? ' is-clash' : '') + '" data-many="keycaps" data-id="' + o.id + '"' + (o.available ? '' : ' disabled') + '>' +
          '<span class="sp2c-kc-img">' + img + (o.compareAt ? '<span class="sp2c-badge sp2c-badge--corner">-' + pct(o.price, o.compareAt) + '%</span>' : '') + '</span>' +
          '<span class="sp2c-name">' + esc(o.label) + '</span><span class="sp2c-delta">' + money(o.price) + '</span>' + why + '</button>';
      }).join('');
      var skip = '<button type="button" class="sp2c-card sp2c-kc sp2c-skip' + (skipped ? ' is-on' : '') + '" data-skip="keycaps"><span class="sp2c-kc-img"><span class="sp2c-noimg">I don’t need any keycaps</span></span><span class="sp2c-name">Skip keycaps</span></button>';
      return '<section class="sp2c-row sp2c-row--wide" data-sp2c-section="keycaps"><div class="sp2c-label">Keycaps <em>' + status + '</em></div><div class="sp2c-opts sp2c-opts--kc">' + cards + skip + '</div></section>';
    }

    // ---------- rendering: summary, phone bar, market (test page) ----------
    function estimateFor(total) {
      if (!estimateCfg.show) return null;
      var r = rates || { rates: { USD: parseFloat(estimateCfg.fallbackUsdRate) || 0.25 }, source: 'fixed fallback rate', date: '' };
      var code = r.rates[estCurrency] ? estCurrency : 'USD';
      return { r: r, code: code, text: SP2Currency.estimate(total, code, r) };
    }

    function estimateBlock(total) {
      var e = estimateFor(total);
      if (!e) return '';
      var opts = SP2Currency.COMMON.filter(function (c) { return e.r.rates[c]; }).map(function (c) {
        return '<option value="' + c + '"' + (c === e.code ? ' selected' : '') + '>' + c + '</option>';
      }).join('');
      var note = 'Estimate only: you’re charged in MYR. Rate: ' + e.r.source + (e.r.date ? ', ' + e.r.date : '') + '.';
      return '<div class="sp2c-estimate" title="' + esc(note) + '"><span>' + (e.text ? esc(e.text) : '') + '</span>' +
        (opts ? '<select data-sp2c-currency aria-label="Estimate currency">' + opts + '</select>' : '') + '</div>' +
        '<p class="sp2c-help sp2c-help--right">Estimate only: charged in MYR.</p>';
    }

    function summaryHtml(ev) {
      var lines = ev.lines.map(function (l) {
        return '<tr><td>' + esc(l.label) + (l.qty > 1 ? ' × ' + l.qty : '') + '</td><td>' + (l.amount ? fmt(l.amount) : '—') + '</td></tr>';
      }).join('');
      var issues = ev.issues.filter(function (i) { return i.code !== 'consent'; });
      var issueHtml = issues.map(function (i) { return '<li class="is-' + i.level + '">' + esc(i.message) + '</li>'; }).join('');
      var share = location.origin + location.pathname + '?sp2=' + engine.encode(state);
      var cart = ui.cart ? '<p class="sp2c-help">Test page: this would be sent to /cart/add.js</p><pre class="sp2c-pre">' + esc(JSON.stringify({ items: ui.cart }, null, 1)) + '</pre>' : '';
      var ok = ev.canAddToCart && !ui.busy;
      return '<div class="sp2c-sum-head"><h2>' + esc(ev.title) + '</h2><span>' + esc(ev.subtitle) + '</span></div>' +
        '<table class="sp2c-lines">' + lines + '</table>' +
        '<div class="sp2c-total"><span>Estimated total</span><b>' + fmt(ev.total) + '</b></div>' +
        estimateBlock(ev.total) +
        (issueHtml ? '<ul class="sp2c-issues">' + issueHtml + '</ul>' : '') +
        '<label class="sp2c-consent"><input type="checkbox" data-sp2c-consent' + (state.consent ? ' checked' : '') + '><span>' + esc(text.consent) + '</span></label>' +
        '<button type="button" class="sp2c-add" data-sp2c-add' + (ok ? '' : ' disabled') + '>' + (ui.busy ? 'Adding to cart…' : 'Add to cart') + '</button>' +
        (cfg.paypal ? '<button type="button" class="sp2c-paypal" data-sp2c-paypal' + (ok ? '' : ' disabled') + '>Buy with PayPal / Card</button>' : '') +
        '<p class="sp2c-help sp2c-help--center">' + esc(text.shippingNote) + '</p>' +
        (ui.feedback ? '<p class="sp2c-feedback is-' + ui.feedbackKind + '">' + esc(ui.feedback) + '</p>' : '') +
        cart +
        '<div class="sp2c-share"><input readonly value="' + esc(share) + '" aria-label="Link to this build"><button type="button" data-sp2c-copy>' + (ui.copied ? 'Copied' : 'Copy link') + '</button></div>';
    }

    function barHtml(ev) {
      var e = estimateFor(ev.total);
      var ready = ev.canAddToCart && !ui.busy;
      return '<div class="sp2c-bar-total"><span>' + esc(ev.title) + '</span><b>' + fmt(ev.total) + '</b>' +
        (e && e.text ? '<small>' + esc(e.text) + '</small>' : '') + '</div>' +
        (ready ? '<button type="button" class="sp2c-add" data-sp2c-add>Add to cart</button>'
               : '<button type="button" class="sp2c-add sp2c-add--review" data-sp2c-review>Review build</button>');
    }

    function marketHtml() {
      return 'Test page: preview prices as a visitor from <select data-sp2c-market>' +
        '<option value="MY"' + (country === 'MY' ? ' selected' : '') + '>Malaysia</option>' +
        '<option value="US"' + (country !== 'MY' ? ' selected' : '') + '>outside Malaysia (international market)</option></select> ' +
        '<span>' + esc(ui.liveNote) + '</span>';
    }

    function render() {
      var ev = engine.evaluate(state);
      var kitMode = ev.route === 'kit';
      if (stage) stage.classList.toggle('is-kit', kitMode);
      // In the kit, plate, bottom, case and foam are optional: each one is included or left out.
      builder.innerHTML = setsRow(ev) +
        '<div class="sp2c-parts">' + ['socket', 'plate', 'bottom', 'case', 'foam'].map(function (s) {
          return kitMode && s !== 'socket' ? partRow(ev, s, !!ev.kit.parts[s]) : partRow(ev, s);
        }).join('') +
        '<p class="sp2c-help sp2c-note">' + esc(text.trackpadNote) + '</p></div>' +
        switchesRow(ev) + keycapsRow(ev);
      summaryEl.innerHTML = summaryHtml(ev);
      if (bar) bar.innerHTML = barHtml(ev);
      if (marketBox) marketBox.innerHTML = marketHtml();
    }

    function update(next) {
      if (next === state) return;
      state = next;
      ui.cart = null; ui.feedback = ''; ui.copied = false;
      params.set('sp2', engine.encode(state));
      history.replaceState(history.state, '', location.pathname + '?' + params.toString()
        .replace(/%7E/gi, '~') + location.hash);
      render();
      if (viewer) { show3D(); viewer.show(engine.selection(state)); syncLayerButtons(); }
    }

    // ---------- cart ----------
    function addToCart(redirect) {
      var items = engine.cartItems(state, engine.newBuildId());
      if (!items) return;
      if (cfg.mode !== 'shopify') { ui.cart = items; render(); return; }
      ui.busy = true; ui.feedback = ''; render();
      fetch(routeRoot() + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // the cart lists the newest line first: send in reverse so the keyboard leads and the keycaps come last
        body: JSON.stringify({ items: items.slice().reverse() })
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Couldn’t add this build to the cart.');
          location.href = routeRoot() + redirect;
        });
      }).catch(function (err) {
        ui.busy = false; ui.feedback = err.message + ' Please try again.'; ui.feedbackKind = 'error'; render();
      });
    }

    // first thing still missing, for the phone bar's "Review build"
    function review() {
      var ev = engine.evaluate(state);
      var first = ev.issues.filter(function (i) { return i.code !== 'consent'; })[0];
      var target = first && (first.slot === 'switches' || first.slot === 'keycaps') ? $('[data-sp2c-section="' + first.slot + '"]') : summaryEl;
      var top = (parseFloat(getComputedStyle(el).getPropertyValue('--sp2c-top')) || 0) + (phone.matches ? stage.offsetHeight : 0) + 12;
      root.scrollTo({ top: target.getBoundingClientRect().top + root.pageYOffset - top, behavior: 'smooth' });
    }

    // ---------- events (one delegated handler for the whole block) ----------
    el.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b || b.disabled) return;
      var tool = b.getAttribute('data-tool');
      if (tool) {
        if (tool === 'photos') { showPhoto(photoIndex); return; }
        if (!viewer) return;
        show3D();
        if (tool.indexOf('view:') === 0) {
          if (tool === 'view:home') { press('explode', false); viewer.setExploded(false); }
          viewer.view(tool.slice(5));
          return;
        }
        var on = b.getAttribute('aria-pressed') !== 'true';
        b.setAttribute('aria-pressed', on);
        if (tool === 'explode') viewer.setExploded(on);
        else if (tool === 'rgb') viewer.setRGB(on);
        else if (tool === 'keycaps') viewer.setKeycaps(on);
        else if (tool === 'switches') viewer.setSwitches(on);
        return;
      }
      var gal = b.getAttribute('data-sp2c-gal');
      if (gal === 'prev') return showPhoto(photoIndex - 1);
      if (gal === 'next') return showPhoto(photoIndex + 1);
      if (gal === 'close') return show3D();
      if (b.hasAttribute('data-sp2c-thumb')) return showPhoto(thumbs().indexOf(b));
      if (b.dataset.set) update(engine.applyPreset(state, b.dataset.set));
      else if (b.hasAttribute('data-kit')) update(engine.setKit(state, true));
      else if (b.dataset.part) {
        var slot = b.dataset.part;
        // In the kit, tapping the part that is already included leaves it out; any other choice includes it
        if (state.kit && state.kitParts[slot] && state.parts[slot] === b.dataset.id) update(engine.setKitPart(state, slot, false));
        else update(engine.setPart(state.kit ? engine.setKitPart(state, slot, true) : state, slot, b.dataset.id));
      }
      else if (b.dataset.many) update(engine.toggle(state, b.dataset.many, b.dataset.id));
      else if (b.dataset.skip) update(engine.skip(state, b.dataset.skip, !state[b.dataset.skip + 'Skipped']));
      else if (b.hasAttribute('data-sp2c-add')) addToCart('cart');
      else if (b.hasAttribute('data-sp2c-paypal')) addToCart('checkout?payment=paypal');
      else if (b.hasAttribute('data-sp2c-review')) review();
      else if (b.hasAttribute('data-sp2c-copy')) {
        var input = b.previousElementSibling;
        (navigator.clipboard ? navigator.clipboard.writeText(input.value) : Promise.reject()).then(function () {
          ui.copied = true; render();
        }, function () { input.select(); });
      }
    });
    el.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-sp2c-consent')) update(engine.setConsent(state, t.checked));
      else if (t.hasAttribute('data-sp2c-currency')) { estCurrency = t.value; lsSet('sp2_cur', estCurrency); render(); }
    });
    if (marketBox) marketBox.addEventListener('change', function (e) {
      if (e.target.hasAttribute('data-sp2c-market')) useMarket(e.target.value);
    });

    // ---------- live data (test page) and exchange rates ----------
    function useMarket(cc) {
      country = cc;
      if (!cfg.loadLive) return Promise.resolve();
      ui.liveNote = 'Loading live prices and stock…'; render();
      return cfg.loadLive(cc).then(function (live) {
        engine = SP2Rules.createEngine(cfg.catalog, live);
        ui.liveNote = 'Live prices and stock from xcmkb.com' + (cc === 'MY' ? '.' : ' (international market, +7% set in Shopify).');
      }, function () {
        engine = SP2Rules.createEngine(cfg.catalog);
        ui.liveNote = 'Couldn’t reach xcmkb.com, so these are the catalog’s fallback prices.';
      }).then(function () {
        state = engine.decode(engine.encode(state));    // re-check picks against live stock
        render();
        if (viewer) { viewer.show(engine.selection(state)); syncLayerButtons(); }
      });
    }
    if (estimateCfg.show) SP2Currency.loadRates().then(function (r) { rates = r; render(); }, function () { /* fallback rate */ });

    render();
    if (cfg.loadLive) useMarket(country);
    var drift = engine.consistency();
    if (drift.length) console.warn('SoflePLUS2 builder: set prices differ from base + parts', drift);
    return {
      get state() { return state; }, get engine() { return engine; }, render: render,
      refreshTools: function () { renderTools(); syncLayerButtons(); }    // after photos are added later
    };
  }

  // Shopify: the theme block carries its data in JSON script tags; mount every builder on the page.
  function autoMount() {
    document.querySelectorAll('[data-sp2c][data-sp2c-auto]').forEach(function (el) {
      if (el.__sp2c) return;
      var read = function (sel) { var s = el.querySelector(sel); return s ? JSON.parse(s.textContent) : null; };
      var conf = root.SP2_CONFIG || {};
      var settings = read('script[data-sp2c-settings]') || {};
      el.__sp2c = mount(el, Object.assign({
        catalog: conf.catalog, looks: conf.looks, hotspots: conf.hotspots,
        live: read('script[data-sp2c-live]') || {}, mode: 'shopify'
      }, settings));
    });
  }

  root.SP2Builder = { mount: mount, autoMount: autoMount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
  else autoMount();
  // theme editor: blocks re-render when their settings change
  document.addEventListener('shopify:section:load', autoMount);
  document.addEventListener('shopify:block:select', autoMount);
})(typeof self !== 'undefined' ? self : this);
